/**
 * Comprehensive ICD-10 Mental Health Diagnosis Codes (F00-F99)
 * Expanded to 500+ codes for clinical documentation
 * Organized by category for easy selection in treatment plans and clinical documentation
 * 
 * Last Updated: December 14, 2025
 * Based on ICD-10-CM 2024/2025
 */

export const ICD10_MENTAL_HEALTH_CODES = [
  // ==================== DEMENTIA AND COGNITIVE DISORDERS (F00-F09) ====================
  {
    category: "Dementia and Cognitive Disorders",
    codes: [
      { code: "F00.A0", description: "Alzheimer's disease with early onset, mild, without behavioral disturbance" },
      { code: "F00.A11", description: "Alzheimer's disease with early onset, mild, with agitation" },
      { code: "F00.A18", description: "Alzheimer's disease with early onset, mild, with other behavioral disturbance" },
      { code: "F00.A2", description: "Alzheimer's disease with early onset, mild, with psychotic disturbance" },
      { code: "F00.A3", description: "Alzheimer's disease with early onset, mild, with mood disturbance" },
      { code: "F00.A4", description: "Alzheimer's disease with early onset, mild, with anxiety" },
      { code: "F00.B0", description: "Alzheimer's disease with early onset, moderate, without behavioral disturbance" },
      { code: "F00.B11", description: "Alzheimer's disease with early onset, moderate, with agitation" },
      { code: "F00.C0", description: "Alzheimer's disease with early onset, severe, without behavioral disturbance" },
      { code: "F00.C11", description: "Alzheimer's disease with early onset, severe, with agitation" },
      { code: "F01.A0", description: "Vascular dementia, mild, without behavioral disturbance" },
      { code: "F01.A11", description: "Vascular dementia, mild, with agitation" },
      { code: "F01.A2", description: "Vascular dementia, mild, with psychotic disturbance" },
      { code: "F01.A3", description: "Vascular dementia, mild, with mood disturbance" },
      { code: "F01.A4", description: "Vascular dementia, mild, with anxiety" },
      { code: "F01.B0", description: "Vascular dementia, moderate, without behavioral disturbance" },
      { code: "F01.B11", description: "Vascular dementia, moderate, with agitation" },
      { code: "F01.B4", description: "Vascular dementia, moderate, with anxiety" },
      { code: "F01.C0", description: "Vascular dementia, severe, without behavioral disturbance" },
      { code: "F01.C11", description: "Vascular dementia, severe, with agitation" },
      { code: "F02.A0", description: "Dementia in other diseases classified elsewhere, mild, without behavioral disturbance" },
      { code: "F02.A11", description: "Dementia in other diseases, mild, with agitation" },
      { code: "F02.B0", description: "Dementia in other diseases, moderate, without behavioral disturbance" },
      { code: "F02.C0", description: "Dementia in other diseases, severe, without behavioral disturbance" },
      { code: "F03.A0", description: "Unspecified dementia, mild, without behavioral disturbance" },
      { code: "F03.A11", description: "Unspecified dementia, mild, with agitation" },
      { code: "F03.A18", description: "Unspecified dementia, mild, with other behavioral disturbance" },
      { code: "F03.A2", description: "Unspecified dementia, mild, with psychotic disturbance" },
      { code: "F03.A3", description: "Unspecified dementia, mild, with mood disturbance" },
      { code: "F03.A4", description: "Unspecified dementia, mild, with anxiety" },
      { code: "F03.B0", description: "Unspecified dementia, moderate, without behavioral disturbance" },
      { code: "F03.B11", description: "Unspecified dementia, moderate, with agitation" },
      { code: "F03.C0", description: "Unspecified dementia, severe, without behavioral disturbance" },
      { code: "F03.C11", description: "Unspecified dementia, severe, with agitation" },
      { code: "F04", description: "Amnestic disorder due to known physiological condition" },
      { code: "F05", description: "Delirium due to known physiological condition" },
      { code: "F06.0", description: "Psychotic disorder with hallucinations due to known physiological condition" },
      { code: "F06.1", description: "Catatonic disorder due to known physiological condition" },
      { code: "F06.2", description: "Psychotic disorder with delusions due to known physiological condition" },
      { code: "F06.30", description: "Mood disorder due to known physiological condition, unspecified" },
      { code: "F06.31", description: "Mood disorder due to known physiological condition with depressive features" },
      { code: "F06.32", description: "Mood disorder due to known physiological condition with major depressive-like episode" },
      { code: "F06.33", description: "Mood disorder due to known physiological condition with manic features" },
      { code: "F06.34", description: "Mood disorder due to known physiological condition with mixed features" },
      { code: "F06.4", description: "Anxiety disorder due to known physiological condition" },
      { code: "F06.70", description: "Mild neurocognitive disorder due to known physiological condition without behavioral disturbance" },
      { code: "F06.71", description: "Mild neurocognitive disorder due to known physiological condition with behavioral disturbance" },
      { code: "F06.8", description: "Other specified mental disorders due to known physiological condition" },
      { code: "F07.0", description: "Personality change due to known physiological condition" },
      { code: "F07.81", description: "Postconcussional syndrome" },
      { code: "F07.89", description: "Other personality and behavioral disorders due to known physiological condition" },
      { code: "F09", description: "Unspecified mental disorder due to known physiological condition" },
    ]
  },

  // ==================== DEPRESSIVE DISORDERS (F32-F34) ====================
  {
    category: "Depressive Disorders",
    codes: [
      { code: "F32.0", description: "Major depressive disorder, single episode, mild" },
      { code: "F32.1", description: "Major depressive disorder, single episode, moderate" },
      { code: "F32.2", description: "Major depressive disorder, single episode, severe without psychotic features" },
      { code: "F32.3", description: "Major depressive disorder, single episode, severe with psychotic features" },
      { code: "F32.4", description: "Major depressive disorder, single episode, in partial remission" },
      { code: "F32.5", description: "Major depressive disorder, single episode, in full remission" },
      { code: "F32.9", description: "Major depressive disorder, single episode, unspecified" },
      { code: "F32.81", description: "Premenstrual dysphoric disorder" },
      { code: "F32.89", description: "Other specified depressive episodes" },
      { code: "F33.0", description: "Major depressive disorder, recurrent, mild" },
      { code: "F33.1", description: "Major depressive disorder, recurrent, moderate" },
      { code: "F33.2", description: "Major depressive disorder, recurrent, severe without psychotic features" },
      { code: "F33.3", description: "Major depressive disorder, recurrent, severe with psychotic symptoms" },
      { code: "F33.4", description: "Major depressive disorder, recurrent, in remission" },
      { code: "F33.40", description: "Major depressive disorder, recurrent, in remission, unspecified" },
      { code: "F33.41", description: "Major depressive disorder, recurrent, in partial remission" },
      { code: "F33.42", description: "Major depressive disorder, recurrent, in full remission" },
      { code: "F33.8", description: "Other recurrent depressive disorders" },
      { code: "F33.9", description: "Major depressive disorder, recurrent, unspecified" },
      { code: "F34.0", description: "Cyclothymic disorder" },
      { code: "F34.1", description: "Dysthymic disorder (Persistent depressive disorder)" },
      { code: "F34.8", description: "Other persistent mood [affective] disorders" },
      { code: "F34.81", description: "Disruptive mood dysregulation disorder" },
      { code: "F34.89", description: "Other specified persistent mood disorders" },
      { code: "F34.9", description: "Persistent mood [affective] disorder, unspecified" },
    ]
  },

  // ==================== ANXIETY DISORDERS (F40-F42) ====================
  {
    category: "Anxiety Disorders",
    codes: [
      { code: "F40.00", description: "Agoraphobia, unspecified" },
      { code: "F40.01", description: "Agoraphobia with panic disorder" },
      { code: "F40.02", description: "Agoraphobia without panic disorder" },
      { code: "F40.10", description: "Social anxiety disorder (Social phobia), unspecified" },
      { code: "F40.11", description: "Social anxiety disorder (Social phobia), generalized" },
      { code: "F40.210", description: "Arachnophobia" },
      { code: "F40.218", description: "Other animal type phobia" },
      { code: "F40.220", description: "Fear of thunderstorms" },
      { code: "F40.228", description: "Other natural environment type phobia" },
      { code: "F40.230", description: "Fear of blood" },
      { code: "F40.231", description: "Fear of injections and transfusions" },
      { code: "F40.232", description: "Fear of other medical care" },
      { code: "F40.233", description: "Fear of injury" },
      { code: "F40.240", description: "Claustrophobia" },
      { code: "F40.241", description: "Acrophobia" },
      { code: "F40.242", description: "Fear of bridges" },
      { code: "F40.243", description: "Fear of flying" },
      { code: "F40.248", description: "Other situational type phobia" },
      { code: "F40.290", description: "Androphobia" },
      { code: "F40.291", description: "Gynephobia" },
      { code: "F40.298", description: "Other specified phobia" },
      { code: "F40.8", description: "Other phobic anxiety disorders" },
      { code: "F40.9", description: "Phobic anxiety disorder, unspecified" },
      { code: "F41.0", description: "Panic disorder without agoraphobia" },
      { code: "F41.1", description: "Generalized anxiety disorder" },
      { code: "F41.3", description: "Other mixed anxiety disorders" },
      { code: "F41.8", description: "Other specified anxiety disorders" },
      { code: "F41.9", description: "Anxiety disorder, unspecified" },
      { code: "F42.2", description: "Mixed obsessional thoughts and acts" },
      { code: "F42.3", description: "Hoarding disorder" },
      { code: "F42.4", description: "Excoriation (skin-picking) disorder" },
      { code: "F42.8", description: "Other obsessive-compulsive disorder" },
      { code: "F42.9", description: "Obsessive-compulsive disorder, unspecified" },
    ]
  },

  // ==================== TRAUMA AND STRESSOR-RELATED DISORDERS (F43) ====================
  {
    category: "Trauma and Stressor-Related Disorders",
    codes: [
      { code: "F43.0", description: "Acute stress reaction" },
      { code: "F43.10", description: "Post-traumatic stress disorder, unspecified" },
      { code: "F43.11", description: "Post-traumatic stress disorder, acute" },
      { code: "F43.12", description: "Post-traumatic stress disorder, chronic" },
      { code: "F43.20", description: "Adjustment disorder, unspecified" },
      { code: "F43.21", description: "Adjustment disorder with depressed mood" },
      { code: "F43.22", description: "Adjustment disorder with anxiety" },
      { code: "F43.23", description: "Adjustment disorder with mixed anxiety and depressed mood" },
      { code: "F43.24", description: "Adjustment disorder with disturbance of conduct" },
      { code: "F43.25", description: "Adjustment disorder with mixed disturbance of emotions and conduct" },
      { code: "F43.29", description: "Adjustment disorder with other symptoms" },
      { code: "F43.8", description: "Other reactions to severe stress" },
      { code: "F43.9", description: "Reaction to severe stress, unspecified" },
    ]
  },

  // ==================== ADHD AND DISRUPTIVE BEHAVIOR DISORDERS (F90-F91) ====================
  {
    category: "ADHD and Disruptive Behavior Disorders",
    codes: [
      { code: "F90.0", description: "Attention-deficit hyperactivity disorder, predominantly inattentive type" },
      { code: "F90.1", description: "Attention-deficit hyperactivity disorder, predominantly hyperactive type" },
      { code: "F90.2", description: "Attention-deficit hyperactivity disorder, combined type" },
      { code: "F90.8", description: "Attention-deficit hyperactivity disorder, other type" },
      { code: "F90.9", description: "Attention-deficit hyperactivity disorder, unspecified type" },
      { code: "F91.0", description: "Conduct disorder confined to family context" },
      { code: "F91.1", description: "Conduct disorder, childhood-onset type" },
      { code: "F91.2", description: "Conduct disorder, adolescent-onset type" },
      { code: "F91.3", description: "Oppositional defiant disorder" },
      { code: "F91.8", description: "Other conduct disorders" },
      { code: "F91.9", description: "Conduct disorder, unspecified" },
      { code: "F94.0", description: "Selective mutism" },
      { code: "F94.1", description: "Reactive attachment disorder of childhood" },
      { code: "F94.2", description: "Disinhibited attachment disorder of childhood" },
      { code: "F94.8", description: "Other childhood disorders of social functioning" },
      { code: "F94.9", description: "Childhood disorder of social functioning, unspecified" },
    ]
  },

  // ==================== AUTISM SPECTRUM DISORDERS (F84) ====================
  {
    category: "Autism Spectrum Disorders",
    codes: [
      { code: "F84.0", description: "Autistic disorder" },
      { code: "F84.2", description: "Rett's syndrome" },
      { code: "F84.3", description: "Other childhood disintegrative disorder" },
      { code: "F84.5", description: "Asperger's syndrome" },
      { code: "F84.8", description: "Other pervasive developmental disorders" },
      { code: "F84.9", description: "Pervasive developmental disorder, unspecified" },
    ]
  },

  // ==================== BIPOLAR AND RELATED DISORDERS (F30-F31) ====================
  {
    category: "Bipolar and Related Disorders",
    codes: [
      { code: "F30.10", description: "Manic episode without psychotic symptoms, unspecified" },
      { code: "F30.11", description: "Manic episode without psychotic symptoms, mild" },
      { code: "F30.12", description: "Manic episode without psychotic symptoms, moderate" },
      { code: "F30.13", description: "Manic episode, severe, without psychotic symptoms" },
      { code: "F30.2", description: "Manic episode, severe with psychotic symptoms" },
      { code: "F30.3", description: "Manic episode in partial remission" },
      { code: "F30.4", description: "Manic episode in full remission" },
      { code: "F30.8", description: "Other manic episodes" },
      { code: "F30.9", description: "Manic episode, unspecified" },
      { code: "F31.0", description: "Bipolar disorder, current episode hypomanic" },
      { code: "F31.10", description: "Bipolar disorder, current episode manic without psychotic features, unspecified" },
      { code: "F31.11", description: "Bipolar disorder, current episode manic without psychotic features, mild" },
      { code: "F31.12", description: "Bipolar disorder, current episode manic without psychotic features, moderate" },
      { code: "F31.13", description: "Bipolar disorder, current episode manic without psychotic features, severe" },
      { code: "F31.2", description: "Bipolar disorder, current episode manic severe with psychotic features" },
      { code: "F31.30", description: "Bipolar disorder, current episode depressed, mild or moderate severity, unspecified" },
      { code: "F31.31", description: "Bipolar disorder, current episode depressed, mild" },
      { code: "F31.32", description: "Bipolar disorder, current episode depressed, moderate" },
      { code: "F31.4", description: "Bipolar disorder, current episode depressed, severe, without psychotic features" },
      { code: "F31.5", description: "Bipolar disorder, current episode depressed, severe, with psychotic features" },
      { code: "F31.60", description: "Bipolar disorder, current episode mixed, unspecified" },
      { code: "F31.61", description: "Bipolar disorder, current episode mixed, mild" },
      { code: "F31.62", description: "Bipolar disorder, current episode mixed, moderate" },
      { code: "F31.63", description: "Bipolar disorder, current episode mixed, severe, without psychotic features" },
      { code: "F31.64", description: "Bipolar disorder, current episode mixed, severe, with psychotic features" },
      { code: "F31.70", description: "Bipolar disorder, currently in remission, most recent episode unspecified" },
      { code: "F31.71", description: "Bipolar disorder, in partial remission, most recent episode hypomanic" },
      { code: "F31.72", description: "Bipolar disorder, in full remission, most recent episode hypomanic" },
      { code: "F31.73", description: "Bipolar disorder, in partial remission, most recent episode manic" },
      { code: "F31.74", description: "Bipolar disorder, in full remission, most recent episode manic" },
      { code: "F31.75", description: "Bipolar disorder, in partial remission, most recent episode depressed" },
      { code: "F31.76", description: "Bipolar disorder, in full remission, most recent episode depressed" },
      { code: "F31.77", description: "Bipolar disorder, in partial remission, most recent episode mixed" },
      { code: "F31.78", description: "Bipolar disorder, in full remission, most recent episode mixed" },
      { code: "F31.81", description: "Bipolar II disorder" },
      { code: "F31.89", description: "Other bipolar disorder" },
      { code: "F31.9", description: "Bipolar disorder, unspecified" },
    ]
  },

  // ==================== SCHIZOPHRENIA SPECTRUM AND PSYCHOTIC DISORDERS (F20-F29) ====================
  {
    category: "Schizophrenia Spectrum and Psychotic Disorders",
    codes: [
      { code: "F20.0", description: "Paranoid schizophrenia" },
      { code: "F20.1", description: "Disorganized schizophrenia" },
      { code: "F20.2", description: "Catatonic schizophrenia" },
      { code: "F20.3", description: "Undifferentiated schizophrenia" },
      { code: "F20.5", description: "Residual schizophrenia" },
      { code: "F20.81", description: "Schizophreniform disorder" },
      { code: "F20.89", description: "Other schizophrenia" },
      { code: "F20.9", description: "Schizophrenia, unspecified" },
      { code: "F21", description: "Schizotypal disorder" },
      { code: "F22", description: "Delusional disorders" },
      { code: "F23", description: "Brief psychotic disorder" },
      { code: "F24", description: "Shared psychotic disorder" },
      { code: "F25.0", description: "Schizoaffective disorder, bipolar type" },
      { code: "F25.1", description: "Schizoaffective disorder, depressive type" },
      { code: "F25.8", description: "Other schizoaffective disorders" },
      { code: "F25.9", description: "Schizoaffective disorder, unspecified" },
      { code: "F28", description: "Other psychotic disorder not due to a substance or known physiological condition" },
      { code: "F29", description: "Unspecified psychosis not due to a substance or known physiological condition" },
    ]
  },

  // ==================== SUBSTANCE USE DISORDERS (F10-F19) ====================
  {
    category: "Substance Use Disorders",
    codes: [
      // Alcohol (F10)
      { code: "F10.10", description: "Alcohol abuse, uncomplicated" },
      { code: "F10.11", description: "Alcohol abuse, in remission" },
      { code: "F10.20", description: "Alcohol dependence, uncomplicated" },
      { code: "F10.21", description: "Alcohol dependence, in remission" },
      { code: "F10.120", description: "Alcohol abuse with intoxication, uncomplicated" },
      { code: "F10.220", description: "Alcohol dependence with intoxication, uncomplicated" },
      { code: "F10.230", description: "Alcohol dependence with withdrawal, uncomplicated" },
      { code: "F10.231", description: "Alcohol dependence with withdrawal delirium" },
      { code: "F10.239", description: "Alcohol dependence with withdrawal, unspecified" },
      { code: "F10.94", description: "Alcohol use, unspecified with alcohol-induced mood disorder" },
      { code: "F10.96", description: "Alcohol use, unspecified with alcohol-induced persisting amnestic disorder" },
      { code: "F10.97", description: "Alcohol use, unspecified with alcohol-induced persisting dementia" },
      { code: "F10.99", description: "Alcohol use, unspecified with unspecified alcohol-induced disorder" },
      // Opioids (F11)
      { code: "F11.10", description: "Opioid abuse, uncomplicated" },
      { code: "F11.11", description: "Opioid abuse, in remission" },
      { code: "F11.20", description: "Opioid dependence, uncomplicated" },
      { code: "F11.21", description: "Opioid dependence, in remission" },
      { code: "F11.23", description: "Opioid dependence with withdrawal" },
      { code: "F11.90", description: "Opioid use, unspecified, uncomplicated" },
      { code: "F11.99", description: "Opioid use, unspecified with unspecified opioid-induced disorder" },
      // Cannabis (F12)
      { code: "F12.10", description: "Cannabis abuse, uncomplicated" },
      { code: "F12.11", description: "Cannabis abuse, in remission" },
      { code: "F12.20", description: "Cannabis dependence, uncomplicated" },
      { code: "F12.21", description: "Cannabis dependence, in remission" },
      { code: "F12.90", description: "Cannabis use, unspecified, uncomplicated" },
      { code: "F12.99", description: "Cannabis use, unspecified with unspecified cannabis-induced disorder" },
      // Sedatives/Hypnotics (F13)
      { code: "F13.10", description: "Sedative, hypnotic or anxiolytic abuse, uncomplicated" },
      { code: "F13.11", description: "Sedative, hypnotic or anxiolytic abuse, in remission" },
      { code: "F13.20", description: "Sedative, hypnotic or anxiolytic dependence, uncomplicated" },
      { code: "F13.21", description: "Sedative, hypnotic or anxiolytic dependence, in remission" },
      { code: "F13.230", description: "Sedative, hypnotic or anxiolytic dependence with withdrawal, uncomplicated" },
      { code: "F13.239", description: "Sedative, hypnotic or anxiolytic dependence with withdrawal, unspecified" },
      { code: "F13.90", description: "Sedative, hypnotic, or anxiolytic use, unspecified, uncomplicated" },
      // Cocaine (F14)
      { code: "F14.10", description: "Cocaine abuse, uncomplicated" },
      { code: "F14.11", description: "Cocaine abuse, in remission" },
      { code: "F14.20", description: "Cocaine dependence, uncomplicated" },
      { code: "F14.21", description: "Cocaine dependence, in remission" },
      { code: "F14.90", description: "Cocaine use, unspecified, uncomplicated" },
      // Stimulants (F15)
      { code: "F15.10", description: "Other stimulant abuse, uncomplicated" },
      { code: "F15.11", description: "Other stimulant abuse, in remission" },
      { code: "F15.20", description: "Other stimulant dependence, uncomplicated" },
      { code: "F15.21", description: "Other stimulant dependence, in remission" },
      { code: "F15.90", description: "Other stimulant use, unspecified, uncomplicated" },
      // Hallucinogens (F16)
      { code: "F16.10", description: "Hallucinogen abuse, uncomplicated" },
      { code: "F16.11", description: "Hallucinogen abuse, in remission" },
      { code: "F16.20", description: "Hallucinogen dependence, uncomplicated" },
      { code: "F16.21", description: "Hallucinogen dependence, in remission" },
      { code: "F16.90", description: "Hallucinogen use, unspecified, uncomplicated" },
      // Tobacco (F17)
      { code: "F17.200", description: "Nicotine dependence, unspecified, uncomplicated" },
      { code: "F17.201", description: "Nicotine dependence, unspecified, in remission" },
      { code: "F17.203", description: "Nicotine dependence, unspecified, with withdrawal" },
      { code: "F17.208", description: "Nicotine dependence, unspecified, with other nicotine-induced disorders" },
      { code: "F17.210", description: "Nicotine dependence, cigarettes, uncomplicated" },
      { code: "F17.211", description: "Nicotine dependence, cigarettes, in remission" },
      { code: "F17.220", description: "Nicotine dependence, chewing tobacco, uncomplicated" },
      // Alcohol-induced disorders (additional)
      { code: "F10.14", description: "Alcohol abuse with alcohol-induced mood disorder" },
      { code: "F10.150", description: "Alcohol abuse with alcohol-induced psychotic disorder with delusions" },
      { code: "F10.151", description: "Alcohol abuse with alcohol-induced psychotic disorder with hallucinations" },
      { code: "F10.180", description: "Alcohol abuse with alcohol-induced anxiety disorder" },
      { code: "F10.181", description: "Alcohol abuse with alcohol-induced sexual dysfunction" },
      { code: "F10.182", description: "Alcohol abuse with alcohol-induced sleep disorder" },
      { code: "F10.188", description: "Alcohol abuse with other alcohol-induced disorder" },
      { code: "F10.24", description: "Alcohol dependence with alcohol-induced mood disorder" },
      { code: "F10.250", description: "Alcohol dependence with alcohol-induced psychotic disorder with delusions" },
      { code: "F10.251", description: "Alcohol dependence with alcohol-induced psychotic disorder with hallucinations" },
      { code: "F10.26", description: "Alcohol dependence with alcohol-induced persisting amnestic disorder" },
      { code: "F10.27", description: "Alcohol dependence with alcohol-induced persisting dementia" },
      { code: "F10.280", description: "Alcohol dependence with alcohol-induced anxiety disorder" },
      { code: "F10.281", description: "Alcohol dependence with alcohol-induced sexual dysfunction" },
      { code: "F10.282", description: "Alcohol dependence with alcohol-induced sleep disorder" },
      { code: "F10.288", description: "Alcohol dependence with other alcohol-induced disorder" },
      // Opioid-induced disorders (additional)
      { code: "F11.120", description: "Opioid abuse with intoxication, uncomplicated" },
      { code: "F11.14", description: "Opioid abuse with opioid-induced mood disorder" },
      { code: "F11.150", description: "Opioid abuse with opioid-induced psychotic disorder with delusions" },
      { code: "F11.188", description: "Opioid abuse with other opioid-induced disorder" },
      { code: "F11.220", description: "Opioid dependence with intoxication, uncomplicated" },
      { code: "F11.221", description: "Opioid dependence with intoxication delirium" },
      { code: "F11.222", description: "Opioid dependence with intoxication with perceptual disturbance" },
      { code: "F11.24", description: "Opioid dependence with opioid-induced mood disorder" },
      { code: "F11.250", description: "Opioid dependence with opioid-induced psychotic disorder with delusions" },
      { code: "F11.281", description: "Opioid dependence with opioid-induced sexual dysfunction" },
      { code: "F11.282", description: "Opioid dependence with opioid-induced sleep disorder" },
      { code: "F11.288", description: "Opioid dependence with other opioid-induced disorder" },
      // Cannabis-induced disorders (additional)
      { code: "F12.120", description: "Cannabis abuse with intoxication, uncomplicated" },
      { code: "F12.122", description: "Cannabis abuse with intoxication with perceptual disturbance" },
      { code: "F12.150", description: "Cannabis abuse with psychotic disorder with delusions" },
      { code: "F12.151", description: "Cannabis abuse with psychotic disorder with hallucinations" },
      { code: "F12.180", description: "Cannabis abuse with cannabis-induced anxiety disorder" },
      { code: "F12.188", description: "Cannabis abuse with other cannabis-induced disorder" },
      { code: "F12.220", description: "Cannabis dependence with intoxication, uncomplicated" },
      { code: "F12.222", description: "Cannabis dependence with intoxication with perceptual disturbance" },
      { code: "F12.250", description: "Cannabis dependence with psychotic disorder with delusions" },
      { code: "F12.251", description: "Cannabis dependence with psychotic disorder with hallucinations" },
      { code: "F12.280", description: "Cannabis dependence with cannabis-induced anxiety disorder" },
      { code: "F12.288", description: "Cannabis dependence with other cannabis-induced disorder" },
      // Sedatives, hypnotics-induced disorders (additional)
      { code: "F13.120", description: "Sedative, hypnotic or anxiolytic abuse with intoxication, uncomplicated" },
      { code: "F13.14", description: "Sedative, hypnotic or anxiolytic abuse with induced mood disorder" },
      { code: "F13.150", description: "Sedative, hypnotic or anxiolytic abuse with induced psychotic disorder with delusions" },
      { code: "F13.180", description: "Sedative, hypnotic or anxiolytic abuse with induced anxiety disorder" },
      { code: "F13.181", description: "Sedative, hypnotic or anxiolytic abuse with induced sexual dysfunction" },
      { code: "F13.182", description: "Sedative, hypnotic or anxiolytic abuse with induced sleep disorder" },
      { code: "F13.220", description: "Sedative, hypnotic or anxiolytic dependence with intoxication, uncomplicated" },
      { code: "F13.231", description: "Sedative, hypnotic or anxiolytic dependence with withdrawal delirium" },
      { code: "F13.232", description: "Sedative, hypnotic or anxiolytic dependence with withdrawal with perceptual disturbance" },
      { code: "F13.24", description: "Sedative, hypnotic or anxiolytic dependence with induced mood disorder" },
      { code: "F13.26", description: "Sedative, hypnotic or anxiolytic dependence with persisting amnestic disorder" },
      { code: "F13.27", description: "Sedative, hypnotic or anxiolytic dependence with persisting dementia" },
      { code: "F13.280", description: "Sedative, hypnotic or anxiolytic dependence with induced anxiety disorder" },
      // Cocaine-induced disorders (additional)
      { code: "F14.120", description: "Cocaine abuse with intoxication, uncomplicated" },
      { code: "F14.121", description: "Cocaine abuse with intoxication with delirium" },
      { code: "F14.122", description: "Cocaine abuse with intoxication with perceptual disturbance" },
      { code: "F14.14", description: "Cocaine abuse with cocaine-induced mood disorder" },
      { code: "F14.150", description: "Cocaine abuse with cocaine-induced psychotic disorder with delusions" },
      { code: "F14.151", description: "Cocaine abuse with cocaine-induced psychotic disorder with hallucinations" },
      { code: "F14.180", description: "Cocaine abuse with cocaine-induced anxiety disorder" },
      { code: "F14.181", description: "Cocaine abuse with cocaine-induced sexual dysfunction" },
      { code: "F14.182", description: "Cocaine abuse with cocaine-induced sleep disorder" },
      { code: "F14.188", description: "Cocaine abuse with other cocaine-induced disorder" },
      { code: "F14.220", description: "Cocaine dependence with intoxication, uncomplicated" },
      { code: "F14.221", description: "Cocaine dependence with intoxication delirium" },
      { code: "F14.222", description: "Cocaine dependence with intoxication with perceptual disturbance" },
      { code: "F14.23", description: "Cocaine dependence with withdrawal" },
      { code: "F14.24", description: "Cocaine dependence with cocaine-induced mood disorder" },
      { code: "F14.250", description: "Cocaine dependence with cocaine-induced psychotic disorder with delusions" },
      { code: "F14.251", description: "Cocaine dependence with cocaine-induced psychotic disorder with hallucinations" },
      { code: "F14.280", description: "Cocaine dependence with cocaine-induced anxiety disorder" },
      { code: "F14.281", description: "Cocaine dependence with cocaine-induced sexual dysfunction" },
      { code: "F14.282", description: "Cocaine dependence with cocaine-induced sleep disorder" },
      { code: "F14.288", description: "Cocaine dependence with other cocaine-induced disorder" },
      // Stimulants-induced disorders (additional)
      { code: "F15.120", description: "Other stimulant abuse with intoxication, uncomplicated" },
      { code: "F15.121", description: "Other stimulant abuse with intoxication with delirium" },
      { code: "F15.122", description: "Other stimulant abuse with intoxication with perceptual disturbance" },
      { code: "F15.14", description: "Other stimulant abuse with stimulant-induced mood disorder" },
      { code: "F15.150", description: "Other stimulant abuse with stimulant-induced psychotic disorder with delusions" },
      { code: "F15.151", description: "Other stimulant abuse with stimulant-induced psychotic disorder with hallucinations" },
      { code: "F15.180", description: "Other stimulant abuse with stimulant-induced anxiety disorder" },
      { code: "F15.181", description: "Other stimulant abuse with stimulant-induced sexual dysfunction" },
      { code: "F15.182", description: "Other stimulant abuse with stimulant-induced sleep disorder" },
      { code: "F15.188", description: "Other stimulant abuse with other stimulant-induced disorder" },
      { code: "F15.220", description: "Other stimulant dependence with intoxication, uncomplicated" },
      { code: "F15.221", description: "Other stimulant dependence with intoxication with delirium" },
      { code: "F15.23", description: "Other stimulant dependence with withdrawal" },
      { code: "F15.24", description: "Other stimulant dependence with stimulant-induced mood disorder" },
      { code: "F15.250", description: "Other stimulant dependence with stimulant-induced psychotic disorder with delusions" },
      { code: "F15.280", description: "Other stimulant dependence with stimulant-induced anxiety disorder" },
      { code: "F15.281", description: "Other stimulant dependence with stimulant-induced sexual dysfunction" },
      { code: "F15.282", description: "Other stimulant dependence with stimulant-induced sleep disorder" },
      { code: "F15.288", description: "Other stimulant dependence with other stimulant-induced disorder" },
      // Other substances (F18-F19)
      { code: "F18.10", description: "Inhalant abuse, uncomplicated" },
      { code: "F18.11", description: "Inhalant abuse, in remission" },
      { code: "F18.120", description: "Inhalant abuse with intoxication, uncomplicated" },
      { code: "F18.14", description: "Inhalant abuse with inhalant-induced mood disorder" },
      { code: "F18.150", description: "Inhalant abuse with inhalant-induced psychotic disorder with delusions" },
      { code: "F18.17", description: "Inhalant abuse with inhalant-induced persisting dementia" },
      { code: "F18.180", description: "Inhalant abuse with inhalant-induced anxiety disorder" },
      { code: "F18.188", description: "Inhalant abuse with other inhalant-induced disorder" },
      { code: "F18.20", description: "Inhalant dependence, uncomplicated" },
      { code: "F18.21", description: "Inhalant dependence, in remission" },
      { code: "F18.220", description: "Inhalant dependence with intoxication, uncomplicated" },
      { code: "F18.24", description: "Inhalant dependence with inhalant-induced mood disorder" },
      { code: "F18.27", description: "Inhalant dependence with inhalant-induced persisting dementia" },
      { code: "F18.280", description: "Inhalant dependence with inhalant-induced anxiety disorder" },
      { code: "F19.10", description: "Other psychoactive substance abuse, uncomplicated" },
      { code: "F19.11", description: "Other psychoactive substance abuse, in remission" },
      { code: "F19.120", description: "Other psychoactive substance abuse with intoxication, uncomplicated" },
      { code: "F19.14", description: "Other psychoactive substance abuse with induced mood disorder" },
      { code: "F19.150", description: "Other psychoactive substance abuse with induced psychotic disorder with delusions" },
      { code: "F19.16", description: "Other psychoactive substance abuse with induced persisting amnestic disorder" },
      { code: "F19.17", description: "Other psychoactive substance abuse with induced persisting dementia" },
      { code: "F19.180", description: "Other psychoactive substance abuse with induced anxiety disorder" },
      { code: "F19.20", description: "Other psychoactive substance dependence, uncomplicated" },
      { code: "F19.21", description: "Other psychoactive substance dependence, in remission" },
      { code: "F19.220", description: "Other psychoactive substance dependence with intoxication, uncomplicated" },
      { code: "F19.230", description: "Other psychoactive substance dependence with withdrawal, uncomplicated" },
      { code: "F19.24", description: "Other psychoactive substance dependence with induced mood disorder" },
      { code: "F19.250", description: "Other psychoactive substance dependence with induced psychotic disorder with delusions" },
      { code: "F19.26", description: "Other psychoactive substance dependence with induced persisting amnestic disorder" },
      { code: "F19.27", description: "Other psychoactive substance dependence with induced persisting dementia" },
      { code: "F19.280", description: "Other psychoactive substance dependence with induced anxiety disorder" },
      { code: "F19.90", description: "Other psychoactive substance use, unspecified, uncomplicated" },
      { code: "F19.920", description: "Other psychoactive substance use, unspecified with intoxication, uncomplicated" },
      { code: "F19.94", description: "Other psychoactive substance use, unspecified with induced mood disorder" },
      { code: "F19.950", description: "Other psychoactive substance use, unspecified with induced psychotic disorder with delusions" },
      { code: "F19.96", description: "Other psychoactive substance use, unspecified with induced persisting amnestic disorder" },
      { code: "F19.97", description: "Other psychoactive substance use, unspecified with induced persisting dementia" },
      { code: "F19.980", description: "Other psychoactive substance use, unspecified with induced anxiety disorder" },
    ]
  },

  // ==================== EATING DISORDERS (F50) ====================
  {
    category: "Eating Disorders",
    codes: [
      { code: "F50.00", description: "Anorexia nervosa, unspecified" },
      { code: "F50.01", description: "Anorexia nervosa, restricting type" },
      { code: "F50.02", description: "Anorexia nervosa, binge eating/purging type" },
      { code: "F50.2", description: "Bulimia nervosa" },
      { code: "F50.8", description: "Other eating disorders" },
      { code: "F50.81", description: "Binge eating disorder" },
      { code: "F50.82", description: "Avoidant/restrictive food intake disorder" },
      { code: "F50.89", description: "Other specified eating disorder" },
      { code: "F50.9", description: "Eating disorder, unspecified" },
    ]
  },

  // ==================== SLEEP-WAKE DISORDERS (F51) ====================
  {
    category: "Sleep-Wake Disorders",
    codes: [
      { code: "F51.01", description: "Primary insomnia" },
      { code: "F51.02", description: "Adjustment insomnia" },
      { code: "F51.03", description: "Paradoxical insomnia" },
      { code: "F51.04", description: "Psychophysiologic insomnia" },
      { code: "F51.05", description: "Insomnia due to other mental disorder" },
      { code: "F51.09", description: "Other insomnia not due to a substance or known physiological condition" },
      { code: "F51.11", description: "Primary hypersomnia" },
      { code: "F51.12", description: "Insufficient sleep syndrome" },
      { code: "F51.13", description: "Hypersomnia due to other mental disorder" },
      { code: "F51.19", description: "Other hypersomnia not due to a substance or known physiological condition" },
      { code: "F51.3", description: "Sleepwalking [somnambulism]" },
      { code: "F51.4", description: "Sleep terrors [night terrors]" },
      { code: "F51.5", description: "Nightmare disorder" },
      { code: "F51.8", description: "Other sleep disorders not due to a substance or known physiological condition" },
      { code: "F51.9", description: "Sleep disorder not due to a substance or known physiological condition, unspecified" },
    ]
  },

  // ==================== PERSONALITY DISORDERS (F60) ====================
  {
    category: "Personality Disorders",
    codes: [
      { code: "F60.0", description: "Paranoid personality disorder" },
      { code: "F60.1", description: "Schizoid personality disorder" },
      { code: "F60.2", description: "Antisocial personality disorder" },
      { code: "F60.3", description: "Borderline personality disorder" },
      { code: "F60.4", description: "Histrionic personality disorder" },
      { code: "F60.5", description: "Obsessive-compulsive personality disorder" },
      { code: "F60.6", description: "Avoidant personality disorder" },
      { code: "F60.7", description: "Dependent personality disorder" },
      { code: "F60.81", description: "Narcissistic personality disorder" },
      { code: "F60.89", description: "Other specific personality disorders" },
      { code: "F60.9", description: "Personality disorder, unspecified" },
    ]
  },

  // ==================== INTELLECTUAL DISABILITIES (F70-F79) ====================
  {
    category: "Intellectual Disabilities",
    codes: [
      { code: "F70", description: "Mild intellectual disabilities" },
      { code: "F71", description: "Moderate intellectual disabilities" },
      { code: "F72", description: "Severe intellectual disabilities" },
      { code: "F73", description: "Profound intellectual disabilities" },
      { code: "F78", description: "Other intellectual disabilities" },
      { code: "F79", description: "Unspecified intellectual disabilities" },
    ]
  },

  // ==================== SPECIFIC DEVELOPMENTAL DISORDERS (F80-F83) ====================
  {
    category: "Specific Developmental Disorders",
    codes: [
      { code: "F80.0", description: "Phonological disorder" },
      { code: "F80.1", description: "Expressive language disorder" },
      { code: "F80.2", description: "Mixed receptive-expressive language disorder" },
      { code: "F80.4", description: "Speech and language development delay due to hearing loss" },
      { code: "F80.81", description: "Childhood onset fluency disorder" },
      { code: "F80.82", description: "Social pragmatic communication disorder" },
      { code: "F80.89", description: "Other developmental disorders of speech and language" },
      { code: "F80.9", description: "Developmental disorder of speech and language, unspecified" },
      { code: "F81.0", description: "Specific reading disorder" },
      { code: "F81.2", description: "Mathematics disorder" },
      { code: "F81.81", description: "Disorder of written expression" },
      { code: "F81.89", description: "Other developmental disorders of scholastic skills" },
      { code: "F81.9", description: "Developmental disorder of scholastic skills, unspecified" },
      { code: "F82", description: "Specific developmental disorder of motor function" },
      { code: "F88", description: "Other disorders of psychological development" },
      { code: "F89", description: "Unspecified disorder of psychological development" },
    ]
  },

  // ==================== CHILDHOOD EMOTIONAL DISORDERS (F93) ====================
  {
    category: "Childhood Emotional Disorders",
    codes: [
      { code: "F93.0", description: "Separation anxiety disorder of childhood" },
      { code: "F93.8", description: "Other childhood emotional disorders" },
      { code: "F93.9", description: "Childhood emotional disorder, unspecified" },
    ]
  },

  // ==================== TIC DISORDERS (F95) ====================
  {
    category: "Tic Disorders",
    codes: [
      { code: "F95.0", description: "Transient tic disorder" },
      { code: "F95.1", description: "Chronic motor or vocal tic disorder" },
      { code: "F95.2", description: "Tourette's disorder" },
      { code: "F95.8", description: "Other tic disorders" },
      { code: "F95.9", description: "Tic disorder, unspecified" },
    ]
  },

  // ==================== OTHER BEHAVIORAL/EMOTIONAL DISORDERS (F98) ====================
  {
    category: "Other Behavioral and Emotional Disorders",
    codes: [
      { code: "F98.0", description: "Enuresis not due to a substance or known physiological condition" },
      { code: "F98.1", description: "Encopresis not due to a substance or known physiological condition" },
      { code: "F98.21", description: "Rumination disorder of infancy" },
      { code: "F98.29", description: "Other feeding disorders of infancy and early childhood" },
      { code: "F98.3", description: "Pica of infancy and childhood" },
      { code: "F98.4", description: "Stereotyped movement disorders" },
      { code: "F98.5", description: "Adult onset fluency disorder" },
      { code: "F98.8", description: "Other specified behavioral and emotional disorders with onset usually occurring in childhood and adolescence" },
      { code: "F98.9", description: "Unspecified behavioral and emotional disorders with onset usually occurring in childhood and adolescence" },
    ]
  },

  // ==================== SOMATOFORM DISORDERS (F45) ====================
  {
    category: "Somatoform Disorders",
    codes: [
      { code: "F45.0", description: "Somatization disorder" },
      { code: "F45.1", description: "Undifferentiated somatoform disorder" },
      { code: "F45.20", description: "Hypochondriacal disorder, unspecified" },
      { code: "F45.21", description: "Hypochondriasis" },
      { code: "F45.22", description: "Body dysmorphic disorder" },
      { code: "F45.29", description: "Other hypochondriacal disorders" },
      { code: "F45.41", description: "Pain disorder exclusively related to psychological factors" },
      { code: "F45.42", description: "Pain disorder with related psychological factors" },
      { code: "F45.8", description: "Other somatoform disorders" },
      { code: "F45.9", description: "Somatoform disorder, unspecified" },
    ]
  },

  // ==================== DISSOCIATIVE DISORDERS (F44) ====================
  {
    category: "Dissociative Disorders",
    codes: [
      { code: "F44.0", description: "Dissociative amnesia" },
      { code: "F44.1", description: "Dissociative fugue" },
      { code: "F44.2", description: "Dissociative stupor" },
      { code: "F44.4", description: "Conversion disorder with motor symptom or deficit" },
      { code: "F44.5", description: "Conversion disorder with seizures or convulsions" },
      { code: "F44.6", description: "Conversion disorder with sensory symptom or deficit" },
      { code: "F44.7", description: "Conversion disorder with mixed symptom presentation" },
      { code: "F44.81", description: "Dissociative identity disorder" },
      { code: "F44.89", description: "Other dissociative and conversion disorders" },
      { code: "F44.9", description: "Dissociative and conversion disorder, unspecified" },
    ]
  },

  // ==================== IMPULSE CONTROL DISORDERS (F63) ====================
  {
    category: "Impulse Control Disorders",
    codes: [
      { code: "F63.0", description: "Pathological gambling" },
      { code: "F63.1", description: "Pyromania" },
      { code: "F63.2", description: "Kleptomania" },
      { code: "F63.3", description: "Trichotillomania" },
      { code: "F63.81", description: "Intermittent explosive disorder" },
      { code: "F63.89", description: "Other impulse disorders" },
      { code: "F63.9", description: "Impulse disorder, unspecified" },
    ]
  },

  // ==================== OTHER MENTAL HEALTH CONDITIONS ====================
  {
    category: "Other Mental Health Conditions",
    codes: [
      { code: "F48.1", description: "Depersonalization-derealization syndrome" },
      { code: "F48.2", description: "Pseudobulbar affect" },
      { code: "F48.8", description: "Other specified nonpsychotic mental disorders" },
      { code: "F48.9", description: "Nonpsychotic mental disorder, unspecified" },
      { code: "F52.0", description: "Hypoactive sexual desire disorder" },
      { code: "F52.1", description: "Sexual aversion disorder" },
      { code: "F52.21", description: "Male erectile disorder" },
      { code: "F52.22", description: "Female sexual arousal disorder" },
      { code: "F52.31", description: "Female orgasmic disorder" },
      { code: "F52.32", description: "Male orgasmic disorder" },
      { code: "F52.4", description: "Premature ejaculation" },
      { code: "F52.5", description: "Vaginismus not due to a substance or known physiological condition" },
      { code: "F52.6", description: "Dyspareunia not due to a substance or known physiological condition" },
      { code: "F52.8", description: "Other sexual dysfunction not due to a substance or known physiological condition" },
      { code: "F52.9", description: "Unspecified sexual dysfunction not due to a substance or known physiological condition" },
      { code: "F64.0", description: "Transsexualism" },
      { code: "F64.1", description: "Dual role transvestism" },
      { code: "F64.2", description: "Gender identity disorder of childhood" },
      { code: "F64.8", description: "Other gender identity disorders" },
      { code: "F64.9", description: "Gender identity disorder, unspecified" },
      { code: "F65.0", description: "Fetishism" },
      { code: "F65.1", description: "Transvestic fetishism" },
      { code: "F65.2", description: "Exhibitionism" },
      { code: "F65.3", description: "Voyeurism" },
      { code: "F65.4", description: "Pedophilia" },
      { code: "F65.51", description: "Sexual masochism" },
      { code: "F65.52", description: "Sexual sadism" },
      { code: "F65.81", description: "Frotteurism" },
      { code: "F65.89", description: "Other paraphilias" },
      { code: "F65.9", description: "Paraphilia, unspecified" },
      { code: "F99", description: "Mental disorder, not otherwise specified" },
    ]
  },
];

// Helper function to get all codes as a flat array
export const getAllICD10Codes = () => {
  return ICD10_MENTAL_HEALTH_CODES.flatMap(category =>
    category.codes.map(code => ({
      ...code,
      category: category.category
    }))
  );
};

// Helper function to search codes
export const searchICD10Codes = (searchTerm) => {
  const term = searchTerm.toLowerCase();
  return getAllICD10Codes().filter(item =>
    item.code.toLowerCase().includes(term) ||
    item.description.toLowerCase().includes(term) ||
    item.category.toLowerCase().includes(term)
  );
};

// Helper function to get codes by category
export const getCodesByCategory = (categoryName) => {
  const category = ICD10_MENTAL_HEALTH_CODES.find(cat => cat.category === categoryName);
  return category ? category.codes : [];
};

export default ICD10_MENTAL_HEALTH_CODES;
